import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { showToast } from '../../utils/helpers';

// Move ProductSettings outside the main component
const ProductSettings = React.memo(({ 
  formData, 
  handleAcceptNegotiationsChange, 
  handleMinPriceChange, 
  handleMaxPriceChange,
  togglePickup,
  toggleDelivery
}) => {
  const [priceError, setPriceError] = useState('');
  const [localValidation, setLocalValidation] = useState({
    minPrice: formData.minPrice,
    maxPrice: formData.maxPrice
  });

  // Use refs to track previous values and avoid infinite loops
  const prevMinPriceRef = useRef(formData.minPrice);
  const prevMaxPriceRef = useRef(formData.maxPrice);

  // Simplified validation - only run when prices actually change
  useEffect(() => {
    // Only validate if prices have actually changed
    if (formData.minPrice === prevMinPriceRef.current && 
        formData.maxPrice === prevMaxPriceRef.current) {
      return;
    }

    // Update refs
    prevMinPriceRef.current = formData.minPrice;
    prevMaxPriceRef.current = formData.maxPrice;

    if (formData.acceptNegotiations && formData.minPrice && formData.maxPrice) {
      const minPrice = parseFloat(formData.minPrice);
      const maxPrice = parseFloat(formData.maxPrice);
      
      if (!isNaN(minPrice) && !isNaN(maxPrice) && minPrice >= maxPrice) {
        setPriceError('Minimum price must be lower than maximum price');
      } else {
        setPriceError('');
      }
    } else {
      setPriceError('');
    }
  }, [formData.minPrice, formData.maxPrice, formData.acceptNegotiations]);

  // Remove the complex validation handlers and use simple wrappers
  const handleMinPriceChangeWithValidation = useCallback((e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const decimalParts = value.split('.');
    
    if (decimalParts.length > 2) return;
    if (decimalParts.length === 2 && decimalParts[1].length > 2) return;
    
    // Simply call the parent handler without additional validation
    handleMinPriceChange(e);
  }, [handleMinPriceChange]);

  const handleMaxPriceChangeWithValidation = useCallback((e) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    const decimalParts = value.split('.');
    
    if (decimalParts.length > 2) return;
    if (decimalParts.length === 2 && decimalParts[1].length > 2) return;
    
    // Simply call the parent handler without additional validation
    handleMaxPriceChange(e);
  }, [handleMaxPriceChange]);

  // Auto-adjust prices when they conflict - simplified
  const autoAdjustPrices = useCallback((newMinPrice, newMaxPrice) => {
    const min = parseFloat(newMinPrice);
    const max = parseFloat(newMaxPrice);
    
    if (!isNaN(min) && !isNaN(max) && min >= max) {
      // Suggest a reasonable adjustment (add 10% to min price for max price)
      const suggestedMax = (min * 1.1).toFixed(2);
      return { minPrice: newMinPrice, maxPrice: suggestedMax };
    }
    return null;
  }, []);

  const handleMinPriceBlur = useCallback((e) => {
    if (formData.maxPrice && formData.minPrice) {
      const adjustment = autoAdjustPrices(formData.minPrice, formData.maxPrice);
      if (adjustment) {
        // Update the max price automatically
        handleMaxPriceChange({ target: { value: adjustment.maxPrice } });
        showToast(`Maximum price adjusted to ₦${adjustment.maxPrice} to maintain valid price range`, 'info');
      }
    }
  }, [formData.minPrice, formData.maxPrice, autoAdjustPrices, handleMaxPriceChange]);

  const handleMaxPriceBlur = useCallback((e) => {
    if (formData.minPrice && formData.maxPrice) {
      const adjustment = autoAdjustPrices(formData.minPrice, formData.maxPrice);
      if (adjustment) {
        // Update the min price automatically
        handleMinPriceChange({ target: { value: adjustment.minPrice } });
        showToast(`Minimum price adjusted to ₦${adjustment.minPrice} to maintain valid price range`, 'info');
      }
    }
  }, [formData.minPrice, formData.maxPrice, autoAdjustPrices, handleMinPriceChange]);

  // Check if prices are valid for form submission
  const hasValidPriceRange = useMemo(() => {
    if (!formData.acceptNegotiations) return true;
    if (!formData.minPrice || !formData.maxPrice) return true;
    
    const minPrice = parseFloat(formData.minPrice);
    const maxPrice = parseFloat(formData.maxPrice);
    
    return !isNaN(minPrice) && !isNaN(maxPrice) && minPrice < maxPrice;
  }, [formData.acceptNegotiations, formData.minPrice, formData.maxPrice]);

  console.log('ProductSettings rendering - priceError:', priceError);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center mb-8">
        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4">
          <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Product Configuration</h3>
          <p className="text-gray-600 text-sm">Customize how buyers can purchase your product</p>
        </div>
      </div>

      {/* Price Negotiation Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h4 className="text-base font-semibold text-gray-900 mb-1">Make negotiable</h4>
            <p className="text-gray-600 text-sm">Allow buyers to negotiate the price</p>
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.acceptNegotiations}
              onChange={handleAcceptNegotiationsChange}
              className="sr-only peer"
            />
            <div className="relative w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
          </label>
        </div>

        {formData.acceptNegotiations && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {/* Price Range Validation Message */}
            {priceError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-700 text-sm font-medium">{priceError}</span>
                </div>
                <p className="text-red-600 text-xs mt-1 ml-7">
                  The minimum price must be lower than the maximum price for negotiations to work properly.
                </p>
              </div>
            )}

            {/* Price Range Suggestion */}
            {!priceError && formData.minPrice && formData.maxPrice && hasValidPriceRange && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 text-sm font-medium">Valid price range set</span>
                </div>
                <p className="text-gray-600 text-xs mt-1 ml-7">
                  Buyers can negotiate between ₦{parseFloat(formData.minPrice).toLocaleString()} and ₦{parseFloat(formData.maxPrice).toLocaleString()}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-3">
                  Minimum Price
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-500 font-medium">₦</span>
                  <input
                    type="text"
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                      priceError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    value={formData.minPrice}
                    onChange={handleMinPriceChangeWithValidation}
                    onBlur={handleMinPriceBlur}
                    placeholder="0.00"
                    inputMode="decimal"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Lowest acceptable price for negotiations
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-800 mb-3">
                  Maximum Price
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-500 font-medium">₦</span>
                  <input
                    type="text"
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all ${
                      priceError ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    value={formData.maxPrice}
                    onChange={handleMaxPriceChangeWithValidation}
                    onBlur={handleMaxPriceBlur}
                    placeholder="0.00"
                    inputMode="decimal"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Highest acceptable price for negotiations
                </p>
              </div>
            </div>

            {/* Price Range Summary */}
            {formData.minPrice && formData.maxPrice && !priceError && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 text-sm font-medium">Price Range:</span>
                  <span className="text-gray-600 text-sm">
                    ₦{parseFloat(formData.minPrice).toLocaleString()} - ₦{parseFloat(formData.maxPrice).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-gray-600 text-xs">Range Width:</span>
                  <span className="text-gray-600 text-xs">
                    ₦{(parseFloat(formData.maxPrice) - parseFloat(formData.minPrice)).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delivery Options */}
      <div className="space-y-6">
        <div>
          <h4 className="text-base font-semibold text-gray-900 mb-4">Delivery Options</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                formData.availableForPickup 
                  ? 'border-black bg-gray-50 shadow-sm' 
                  : 'border-gray-200 bg-white hover:border-gray-400'
              }`}
              onClick={togglePickup}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                  formData.availableForPickup 
                    ? 'border-black bg-black' 
                    : 'border-gray-300'
                }`}>
                  {formData.availableForPickup && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  )}
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">Customer Pickup</h5>
                  <p className="text-gray-600 mt-1 text-sm">Buyers collect from your location</p>
                </div>
              </div>
            </div>
            
            <div
              className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                formData.availableForDelivery 
                  ? 'border-black bg-gray-50 shadow-sm' 
                  : 'border-gray-200 bg-white hover:border-gray-400'
              }`}
              onClick={toggleDelivery}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-1 flex-shrink-0 ${
                  formData.availableForDelivery 
                    ? 'border-black bg-black' 
                    : 'border-gray-300'
                }`}>
                  {formData.availableForDelivery && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  )}
                </div>
                <div>
                  <h5 className="font-medium text-gray-900">Home Delivery</h5>
                  <p className="text-gray-600 mt-1 text-sm">We deliver to buyer's address</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProductSettings;